import type {
  AnimalGroupDto,
  FeedComponentDto,
  OrganizationDto,
  RecipeDto,
  UserDataDto,
} from "@strtmn/sds/types"
import { createContext, useState, type PropsWithChildren } from "react"
import { getCurrentUserData } from "../api/userData"
import { isDev } from "../config"

export type IClearUserDataFunc = () => void
type IReloadUserDataFunc = () => Promise<UserDataDto | undefined>

export const reloadUserData: IReloadUserDataFunc = async (): Promise<
  UserDataDto | undefined
> => {
  console.log(`> UserDataContextProvider reloadUserData()`)
  const ignoreCache = isDev
  const userData = await getCurrentUserData(ignoreCache)
  if (!userData) {
    console.log("😕 got no user data")
    return undefined
  }
  // console.log("🎉 got user data")
  console.log("< CTX reloadUserData")
  return userData
}

interface IUserDataContext {
  userData?: UserDataDto
  setUserData?: React.Dispatch<React.SetStateAction<UserDataDto | undefined>>
  clearUserData?: IClearUserDataFunc
  reloadUserDataAsync: IReloadUserDataFunc
}
export const UserDataContext = createContext<IUserDataContext>({
  userData: undefined,
  setUserData: undefined,
  clearUserData: undefined,
  reloadUserDataAsync: reloadUserData,
})

export default function UserDataContextProvider({
  children,
}: PropsWithChildren) {
  const [userData, setUserData] = useState<UserDataDto | undefined>(undefined)

  const clearUserData: IClearUserDataFunc = () => {
    console.log("> UserDataContextProvider.clearUserData")
    setUserData(undefined)
  }
  const reloadAndSetUserData: IReloadUserDataFunc = async () => {
    const updatedUserData = await reloadUserData()
    setUserData(updatedUserData)
    return updatedUserData
  }
  return (
    <UserDataContext.Provider
      value={{
        userData,
        setUserData,
        clearUserData,
        reloadUserDataAsync: reloadAndSetUserData,
      }}
    >
      {children}
    </UserDataContext.Provider>
  )
}

export class UserDataContextHelper {
  static findOrgs(userData: UserDataDto): OrganizationDto[] {
    return userData.Organizations
  }

  static findOrg(orgId: string, userData: UserDataDto): OrganizationDto {
    const orgs = userData.Organizations
    const orgsFiltered = orgs.filter((o) => o.id === orgId)
    if (!orgsFiltered) {
      throw new Error("findOrg: orgsFiltered is not set")
    }
    if (0 === orgsFiltered.length) {
      throw new Error("findOrg: orgsFiltered is empty")
    }
    if (orgsFiltered.length > 1) {
      throw new Error("findOrg: orgsFiltered is too full")
    }
    return orgsFiltered[0]!
  }

  /**
   * Like `findOrg`, but `undefined` instead of throwing when the user has no
   * org with that id. For callers that get the id from the URL, from a stored
   * preference, or not at all — none of which is an exceptional case.
   */
  static tryFindOrg(
    orgId: string | undefined,
    userData: UserDataDto | undefined
  ): OrganizationDto | undefined {
    if (!orgId || !userData) {
      return undefined
    }
    try {
      return UserDataContextHelper.findOrg(orgId, userData)
    } catch (err) {
      console.debug("tryFindOrg:", err)
      return undefined
    }
  }

  static findAnimalGroups(org: OrganizationDto): AnimalGroupDto[] {
    return org.Ausladegruppen
  }

  static findAnimalGroup(org: OrganizationDto, agId: string): AnimalGroupDto {
    const agsFiltered = org.Ausladegruppen.filter((ag) => ag.id === agId)
    if (!agsFiltered) {
      throw new Error("findAnimalGroup: agsFiltered is not set")
    }
    if (0 === agsFiltered.length) {
      throw new Error("findAnimalGroup: agsFiltered is empty")
    }
    if (agsFiltered.length > 1) {
      throw new Error("findAnimalGroup: agsFiltered is too full")
    }
    return agsFiltered[0]!
  }

  static findFeedComponents(org: OrganizationDto): FeedComponentDto[] {
    return org.FeedComponents
  }

  static findFeedComponent(
    org: OrganizationDto,
    feedComponentId: string
  ): FeedComponentDto {
    const fcsFiltered = org.FeedComponents.filter(
      (fc) => fc.id === feedComponentId
    )
    if (!fcsFiltered) {
      throw new Error("findFeedComponent: fcsFiltered is not set")
    }
    if (0 === fcsFiltered.length) {
      throw new Error("findFeedComponent: fcsFiltered is empty")
    }
    if (fcsFiltered.length > 1) {
      throw new Error("findFeedComponent: fcsFiltered is too full")
    }
    return fcsFiltered[0]!
  }

  static findRecipes(org: OrganizationDto): RecipeDto[] {
    return org.Recipes
  }

  static findRecipe(org: OrganizationDto, recipeId: string): RecipeDto {
    const recipes = this.findRecipes(org)
    const recipesFiltered = recipes.filter((r) => r.id === recipeId)
    if (!recipesFiltered) {
      throw new Error("findRecipe: recipesFiltered is not set")
    }
    if (0 === recipesFiltered.length) {
      throw new Error("findRecipe: recipesFiltered is empty")
    }
    if (recipesFiltered.length > 1) {
      throw new Error("findRecipe: recipesFiltered is too full")
    }
    return recipesFiltered[0]!
  }
}
